import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '@clerk/clerk-react';
import { useNotifications } from '../../../context/NotificationContext';
import { useClientMessages, useRespondToMessage } from '../../../hooks/queries/useClientMessages';
import MessageResponseModal from './MessageResponseModal';
import {
  Mail, Inbox, CheckCircle, Clock, AlertTriangle, ChevronDown, ChevronUp,
  Paperclip, Bell, UserCheck, FileText, Calendar, Check
} from 'lucide-react';

/**
 * Client Messages Section
 * Shows all notifications and messages from MarkoTM attorneys.
 * Combines notifications (status updates) and messages (requiring response).
 */
function ClientMessagesSection() {
  const { t } = useTranslation('dashboard');
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Get both notifications and messages
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { data: messagesData, isLoading: isLoadingMessages } = useClientMessages();
  const messages = messagesData?.messages || [];

  // Categories that clients should see (exclude internal attorney operations)
  const CLIENT_VISIBLE_CATEGORIES = [
    'status_change',      // Application status updates (filed, registered, etc.)
    'filing_update',      // Filing progress updates
    'deadline',           // Deadline reminders relevant to client
    'correspondence',     // IP office correspondence
    'payment',            // Payment-related notifications
    'client_response'     // When attorney responds to client
  ];

  // Categories to hide from clients (internal attorney operations)
  const INTERNAL_CATEGORIES = [
    'assignment',         // Attorney assigned/reassigned
    'recall',             // Application recalled
    'transfer',           // Application transferred between orgs
    'approval',           // Internal approval workflows
    'internal_request',   // Internal attorney requests
    'new_filing',         // New filing ready (for attorneys)
    'filing_queue'        // Filing queue notifications (for attorneys)
  ];

  // Combine notifications and messages
  const allItems = useMemo(() => {
    // Messages that require response
    const messageItems = messages.map(m => ({
      ...m,
      itemType: 'message',
      isMessage: true,
      displayTitle: m.title || m.subject,
      displayMessage: m.message || m.body,
      createdAt: m.createdAt
    }));

    // Notifications - only show client-visible categories
    const messageIds = new Set(messages.map(m => m.id));
    const notificationItems = notifications
      .filter(n => {
        // Skip if already shown as a message
        if (messageIds.has(n.id)) return false;
        // Skip client_message category (these come through as messages)
        if (n.category === 'client_message') return false;
        // Skip internal attorney categories
        if (INTERNAL_CATEGORIES.includes(n.category)) return false;
        // Also filter by title patterns for any miscategorized notifications
        const title = (n.title || '').toLowerCase();
        if (title.includes('recalled') ||
            title.includes('attorney assigned') ||
            title.includes('transferred') ||
            title.includes('assignment') ||
            title.includes('new filing ready') ||
            title.includes('filing ready')) {
          return false;
        }
        return true;
      })
      .map(n => ({
        ...n,
        itemType: 'notification',
        isMessage: false,
        displayTitle: n.title,
        displayMessage: n.message || n.body,
        createdAt: n.createdAt
      }));

    // Combine and sort by date
    return [...messageItems, ...notificationItems].sort((a, b) =>
      new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [messages, notifications]);

  // Filter items based on tab
  const pendingMessages = messages.filter(m => m.responseStatus === 'pending' && m.responseRequired);
  const unreadItems = allItems.filter(item => !item.read && !item.readAt);
  const displayItems = activeTab === 'pending' ? pendingMessages :
                       activeTab === 'unread' ? unreadItems :
                       allItems;

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const handleMarkAsRead = async (itemId) => {
    await markAsRead(itemId);
  };

  if (!user) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {t('messages.signInRequired', 'Sign in Required')}
        </h2>
        <p className="text-gray-600">
          {t('messages.signInToView', 'Please sign in to view your messages.')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('messages.title', 'Messages')}
            </h1>
            <p className="text-gray-600 mt-1">
              {t('messages.subtitle', 'Notifications and messages from your trademark attorney')}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {pendingMessages.length > 0 && (
              <div className="flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-full">
                <AlertTriangle className="w-4 h-4" />
                <span className="font-medium">
                  {pendingMessages.length} {t('messages.actionRequired', 'response required')}
                </span>
              </div>
            )}
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
              >
                <Check className="w-4 h-4" />
                {t('messages.markAllRead', 'Mark all as read')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs — scroll internally on narrow screens, never page-level */}
      <div className="flex border-b border-gray-200 overflow-x-auto">
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 min-h-[44px] ${
            activeTab === 'pending'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('messages.pendingTab', 'Awaiting Response')}
          {pendingMessages.length > 0 && (
            <span className="ml-2 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs">
              {pendingMessages.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('unread')}
          className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 min-h-[44px] ${
            activeTab === 'unread'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('messages.unreadTab', 'Unread')}
          {unreadItems.length > 0 && (
            <span className="ml-2 bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full text-xs">
              {unreadItems.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 sm:px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 min-h-[44px] ${
            activeTab === 'all'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('messages.allTab', 'All Messages')}
          <span className="ml-2 text-gray-400 text-xs">
            {allItems.length}
          </span>
        </button>
      </div>

      {/* Messages List */}
      <div className="p-4 sm:p-6">
        {isLoadingMessages ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">{t('messages.loading', 'Loading messages...')}</p>
          </div>
        ) : displayItems.length === 0 ? (
          <div className="text-center py-12">
            <Inbox className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {activeTab === 'pending'
                ? t('messages.noPending', 'No messages requiring response')
                : activeTab === 'unread'
                ? t('messages.noUnread', 'All caught up!')
                : t('messages.noMessages', 'No messages yet')}
            </h3>
            <p className="text-gray-600">
              {activeTab === 'pending'
                ? t('messages.emptyStateHint', 'Your attorney will contact you here if they need any information.')
                : t('messages.allReadHint', 'You have no unread notifications.')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {displayItems.map((item) => (
              item.isMessage ? (
                <MessageCard
                  key={item.id}
                  message={item}
                  onRespond={() => setSelectedMessage(item)}
                  onMarkRead={() => handleMarkAsRead(item.id)}
                />
              ) : (
                <NotificationCard
                  key={item.id}
                  notification={item}
                  onMarkRead={() => handleMarkAsRead(item.id)}
                />
              )
            ))}
          </div>
        )}
      </div>

      {/* Response Modal */}
      {selectedMessage && (
        <MessageResponseModal
          isOpen={!!selectedMessage}
          onClose={() => setSelectedMessage(null)}
          message={selectedMessage}
        />
      )}
    </div>
  );
}

/**
 * Notification Card Component (for status updates, not requiring response)
 */
function NotificationCard({ notification, onMarkRead }) {
  const { t } = useTranslation('dashboard');
  const isUnread = !notification.read && !notification.readAt;

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'status_change':
        return <FileText className="w-5 h-5" />;
      case 'assignment':
        return <UserCheck className="w-5 h-5" />;
      case 'deadline':
        return <Calendar className="w-5 h-5" />;
      case 'correspondence':
        return <Mail className="w-5 h-5" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'status_change':
        return 'bg-blue-100 text-blue-600';
      case 'assignment':
        return 'bg-purple-100 text-purple-600';
      case 'deadline':
        return 'bg-amber-100 text-amber-600';
      case 'correspondence':
        return 'bg-teal-100 text-teal-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return t('messages.justNow', 'Just now');
    if (diffHours < 24) return `${diffHours} ${t('messages.hoursAgo', 'hours ago')}`;
    if (diffDays < 7) return `${diffDays} ${t('messages.daysAgo', 'days ago')}`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const handleClick = () => {
    if (isUnread) {
      onMarkRead();
    }
  };

  return (
    <div
      className={`border rounded-lg p-4 transition-colors cursor-pointer ${
        isUnread ? 'border-teal-200 bg-teal-50/30' : 'border-gray-200 hover:bg-gray-50'
      }`}
      onClick={handleClick}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getCategoryColor(notification.category)}`}>
          {getCategoryIcon(notification.category)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className={`font-medium ${isUnread ? 'text-gray-900' : 'text-gray-700'}`}>
                {notification.displayTitle}
              </h3>
              <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                {notification.displayMessage}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-gray-500">
                {formatDate(notification.createdAt)}
              </span>
              {isUnread && (
                <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Message Card Component (for messages requiring response)
 */
function MessageCard({ message, onRespond, onMarkRead }) {
  const { t } = useTranslation('dashboard');
  const [expanded, setExpanded] = useState(false);

  const urgencyStyles = {
    low: 'bg-gray-100 text-gray-700',
    medium: 'bg-blue-100 text-blue-700',
    high: 'bg-amber-100 text-amber-700',
    critical: 'bg-red-100 text-red-700'
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isPending = message.responseRequired && message.responseStatus === 'pending';
  const isUnread = !message.read && !message.readAt;

  const handleToggle = () => {
    if (isUnread && !expanded) {
      onMarkRead();
    }
    setExpanded(!expanded);
  };

  return (
    <div className={`border rounded-lg ${isPending ? 'border-amber-200 bg-amber-50/30' : isUnread ? 'border-teal-200 bg-teal-50/30' : 'border-gray-200'}`}>
      <div
        className="p-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
        onClick={handleToggle}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              isPending ? 'bg-amber-100' : 'bg-teal-100'
            }`}>
              {isPending ? (
                <Clock className="w-5 h-5 text-amber-600" />
              ) : message.responseStatus === 'responded' ? (
                <CheckCircle className="w-5 h-5 text-teal-600" />
              ) : (
                <Mail className="w-5 h-5 text-teal-600" />
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-gray-900 break-words">{message.displayTitle || message.title}</h3>
                {isUnread && !isPending && (
                  <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-0.5">
                {t('messages.from', 'From')}: {message.senderName || 'MarkoTM Attorney'}
              </p>
              {message.applicationId && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {t('messages.application', 'Application')}: {message.applicationId}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 flex-shrink-0">
            {isPending && (
              <span className="text-xs font-medium px-2 py-1 rounded bg-amber-100 text-amber-700">
                {t('messages.responseRequired', 'Response Required')}
              </span>
            )}
            <span className={`text-xs font-medium px-2 py-1 rounded ${urgencyStyles[message.urgency] || urgencyStyles.medium}`}>
              {message.urgency}
            </span>
            <span className="text-xs text-gray-500">
              {formatDate(message.createdAt)}
            </span>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100">
          {/* Message Content */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              {t('messages.messageLabel', 'Message')}
            </h4>
            <p className="text-gray-600 whitespace-pre-wrap">{message.displayMessage || message.message}</p>
          </div>

          {/* Requested Items */}
          {message.requestData?.items && message.requestData.items.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                {t('messages.requestedInfo', 'Information Requested')}
              </h4>
              <ul className="space-y-2">
                {message.requestData.items.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 p-3 bg-white rounded border border-gray-200">
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                      {item.type}
                    </span>
                    <span className="text-gray-700">{item.label}</span>
                    {item.required && (
                      <span className="text-red-500 font-bold">*</span>
                    )}
                    {item.response && (
                      <span className="ml-auto text-teal-600 bg-teal-50 px-2 py-0.5 rounded text-sm">
                        {item.response}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Attachments */}
          {message.attachments && message.attachments.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                {t('messages.attachments', 'Attachments')}
              </h4>
              <ul className="space-y-1">
                {message.attachments.map((att, idx) => (
                  <li key={idx}>
                    <a
                      href={att.blobUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-teal-600 hover:text-teal-700"
                    >
                      <Paperclip className="w-4 h-4" />
                      {att.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Response Status / Actions */}
          {message.responseStatus === 'responded' ? (
            <div className="bg-teal-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-teal-700 mb-2">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">{t('messages.youResponded', 'You responded')}</span>
                <span className="text-sm text-teal-600">
                  {formatDate(message.respondedAt)}
                </span>
              </div>
            </div>
          ) : message.responseRequired ? (
            <div className="flex justify-end">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRespond();
                }}
                className="bg-teal-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-teal-700 transition-colors"
              >
                {t('messages.respond', 'Respond')}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default ClientMessagesSection;
